import React, { useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ApartmentIcon from "@mui/icons-material/Apartment";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CloseIcon from "@mui/icons-material/Close";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import DownloadIcon from "@mui/icons-material/Download";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import { BarChart } from "@mui/x-charts/BarChart";
import { LineChart } from "@mui/x-charts/LineChart";
import moment from "moment-timezone";
import { useGetSuBuildingDataQuery } from "api/homeApi";
import MuiDateRangePicker from "views/common/DateRangePicker";
import { ColorDotPicker } from "components/ColorDotPicker";
import { DEFAULT_COMPARISON_COLORS } from "utils/colorOptions";
import {
  buildBuildingCsv,
  buildBuildingSummaries,
  buildDailyBuildingData,
  buildOverallBuildingSummary,
  getAvailableBuildings,
  normalizeBuildingRows,
} from "utils/suBuildingData";

const TIME_ZONE = "America/New_York";
const GIBIBYTE = 1024 ** 3;

const createTodayRange = () => {
  const today = moment.tz(TIME_ZONE).startOf("day").toDate();
  return { start: { _d: today }, end: { _d: today } };
};

const getApiRange = (range) => {
  if (!range?.start?._d || !range?.end?._d) return null;
  return {
    startDate: moment(range.start._d).tz(TIME_ZONE).format("YYYY-MM-DD"),
    endDate: moment(range.end._d).tz(TIME_ZONE).format("YYYY-MM-DD"),
  };
};

const formatRange = (range) => {
  const apiRange = getApiRange(range);
  if (!apiRange) return "Select a range";
  const start = moment(apiRange.startDate).format("MMM D, YYYY");
  const end = moment(apiRange.endDate).format("MMM D, YYYY");
  return apiRange.startDate === apiRange.endDate ? start : `${start} - ${end}`;
};

const formatCount = (value) => Number(value || 0).toLocaleString("en-US", {
  maximumFractionDigits: 1,
});

const formatCompactCount = (value) => Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
}).format(Number(value || 0));

const formatBytes = (value) => {
  const bytes = Number(value || 0);
  const absoluteBytes = Math.abs(bytes);
  const sign = bytes < 0 ? "-" : "";
  if (absoluteBytes < 1024) return `${formatCount(bytes)} B`;
  const units = ["KB", "MB", "GB", "TB", "PB"];
  let amount = absoluteBytes;
  let unitIndex = -1;
  do {
    amount /= 1024;
    unitIndex += 1;
  } while (amount >= 1024 && unitIndex < units.length - 1);
  return `${sign}${amount.toLocaleString("en-US", { maximumFractionDigits: 1 })} ${units[unitIndex]}`;
};

const getErrorMessage = (error) => {
  if (!error) return "Unable to load building data.";
  if (error.status === 401 || error.status === 403) {
    return "You do not have permission to view SU building data.";
  }
  return error.data?.detail || "Unable to load building data. Please try again.";
};

const ALL_BUILDINGS = "All Buildings";

const SummaryPanel = ({ label, range, summary, accent = "#005AA6" }) => {
  const perBuilding = (value) => (summary.building_count ? Number(value || 0) / summary.building_count : 0);
  const metrics = [
    {
      label: "Buildings",
      value: formatCount(summary.building_count),
      detail: `${formatCount(summary.day_count)} day${summary.day_count === 1 ? "" : "s"}`,
    },
    {
      label: "Avg per building daily users",
      value: formatCount(perBuilding(summary.total_average_users)),
      detail: `AT&T ${formatCount(perBuilding(summary.att_average_users))} | T-Mobile ${formatCount(perBuilding(summary.tmo_average_users))}`,
    },
    {
      label: "Download",
      value: formatBytes(summary.total_rx_data),
      detail: `AT&T ${formatBytes(summary.att_rx_data)} | T-Mobile ${formatBytes(summary.tmo_rx_data)}`,
    },
    {
      label: "Upload",
      value: formatBytes(summary.total_tx_data),
      detail: `AT&T ${formatBytes(summary.att_tx_data)} | T-Mobile ${formatBytes(summary.tmo_tx_data)}`,
    },
  ];

  return (
    <Paper variant="outlined" sx={{ borderRadius: "6px", overflow: "hidden", borderTop: `4px solid ${accent}` }}>
      <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
        <Typography sx={{ color: accent, fontSize: 12, fontWeight: 700, textTransform: "uppercase" }}>
          {label}
        </Typography>
        <Typography sx={{ color: "#0A2240", fontSize: 14, fontWeight: 600 }}>{formatRange(range)}</Typography>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", xl: "repeat(4, minmax(0, 1fr))" },
        }}
      >
        {metrics.map((metric, index) => (
          <Box
            key={metric.label}
            sx={{
              minWidth: 0,
              px: 2,
              py: 2,
              borderRight: { xl: index < metrics.length - 1 ? "1px solid" : "none" },
              borderBottom: { xs: index < metrics.length - 1 ? "1px solid" : "none", xl: "none" },
              borderColor: "divider",
            }}
          >
            <Typography sx={{ color: "text.secondary", fontSize: 12, fontWeight: 600 }}>{metric.label}</Typography>
            <Typography sx={{ color: "#0A2240", fontSize: 24, fontWeight: 700, lineHeight: 1.3 }}>
              {metric.value}
            </Typography>
            <Typography sx={{ color: "text.secondary", fontSize: 11, mt: 0.5 }}>{metric.detail}</Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

const ChartFrame = ({ title, children }) => (
  <Paper variant="outlined" sx={{ borderRadius: "6px", p: { xs: 1.5, sm: 2 }, minWidth: 0 }}>
    <Typography sx={{ color: "#0A2240", fontSize: 16, fontWeight: 700, mb: 1 }}>{title}</Typography>
    <Box sx={{ width: "100%", minWidth: 0, overflow: "hidden" }}>{children}</Box>
  </Paper>
);

const RangeCharts = ({ rows, compact = false }) => {
  const dailyRows = useMemo(() => buildDailyBuildingData(rows), [rows]);
  const dataset = useMemo(() => dailyRows.map((row) => ({
    date: moment(row.est_day).format("MMM D"),
    attUsers: row.att_unique_users,
    tmoUsers: row.tmo_unique_users,
    attRx: row.att_rx_data / GIBIBYTE,
    attTx: row.att_tx_data / GIBIBYTE,
    tmoRx: row.tmo_rx_data / GIBIBYTE,
    tmoTx: row.tmo_tx_data / GIBIBYTE,
  })), [dailyRows]);

  if (!dataset.length) return <Alert severity="info">No building records were found for this range.</Alert>;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: compact ? "minmax(0, 1fr)" : { xs: "minmax(0, 1fr)", xl: "repeat(2, minmax(0, 1fr))" },
        gap: 2,
      }}
    >
      <ChartFrame title="Daily user counts">
        <BarChart
          dataset={dataset}
          xAxis={[{ dataKey: "date", scaleType: "band", tickLabelStyle: { fontWeight: 600 } }]}
          yAxis={[{ valueFormatter: formatCompactCount }]}
          series={[
            { dataKey: "attUsers", label: "AT&T", color: "#00A8E1", stack: "users" },
            { dataKey: "tmoUsers", label: "T-Mobile", color: "#E20074", stack: "users" },
          ]}
          height={330}
        />
      </ChartFrame>
      <ChartFrame title="Daily data usage (GB)">
        <LineChart
          dataset={dataset}
          xAxis={[{ dataKey: "date", scaleType: "band", tickLabelStyle: { fontWeight: 600 } }]}
          yAxis={[{ valueFormatter: (value) => `${formatCompactCount(value)} GB`, width: 70 }]}
          series={[
            { dataKey: "attRx", label: "AT&T RX", color: "#0077A8", curve: "linear" },
            { dataKey: "attTx", label: "AT&T TX", color: "#65C7E8", curve: "linear" },
            { dataKey: "tmoRx", label: "T-Mobile RX", color: "#C00062", curve: "linear" },
            { dataKey: "tmoTx", label: "T-Mobile TX", color: "#F184B8", curve: "linear" },
          ]}
          height={330}
        />
      </ChartFrame>
    </Box>
  );
};

const DeltaValue = ({ first, second, formatter = formatCount }) => {
  const delta = Number(second || 0) - Number(first || 0);
  const percent = Number(first) ? (delta / Number(first)) * 100 : null;
  const color = delta > 0 ? "#1B5E20" : delta < 0 ? "#B3261E" : "text.secondary";

  return (
    <Box sx={{ color, fontWeight: 700, whiteSpace: "nowrap" }}>
      {delta > 0 ? "+" : ""}{formatter(delta)}
      <Typography component="span" sx={{ color: "text.secondary", fontSize: 11, ml: 0.5 }}>
        {percent == null ? "" : `(${percent > 0 ? "+" : ""}${percent.toFixed(1)}%)`}
      </Typography>
    </Box>
  );
};

const CARRIER_ROWS = [
  {
    key: "att",
    label: "AT&T",
    color: "#00A8E1",
    usersKey: "att_average_users",
    downloadKey: "att_rx_data",
    uploadKey: "att_tx_data",
  },
  {
    key: "tmo",
    label: "T-Mobile",
    color: "#E20074",
    usersKey: "tmo_average_users",
    downloadKey: "tmo_rx_data",
    uploadKey: "tmo_tx_data",
  },
  {
    key: "total",
    label: "Combined",
    color: "#0A2240",
    usersKey: "total_average_users",
    downloadKey: "total_rx_data",
    uploadKey: "total_tx_data",
  },
];

const hasMetricValue = (value) => value !== null
  && value !== undefined
  && Number.isFinite(Number(value));

const MetricValue = ({ value, formatter }) => (
  <Typography sx={{ fontSize: 14, fontWeight: 700, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
    {hasMetricValue(value) ? formatter(value) : "--"}
  </Typography>
);

const ComparisonMetric = ({ first, second, formatter, colors }) => {
  const hasFirst = hasMetricValue(first);
  const hasSecond = hasMetricValue(second);

  return (
    <Stack alignItems="flex-end" sx={{ minWidth: 120 }}>
      <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
        <Typography sx={{ color: colors.first, fontSize: 14, fontWeight: 700 }}>
          {hasFirst ? formatter(first) : "--"}
        </Typography>
        <Typography aria-hidden="true" sx={{ color: "#7A869A", fontSize: 12 }}>/</Typography>
        <Typography sx={{ color: colors.second, fontSize: 14, fontWeight: 700 }}>
          {hasSecond ? formatter(second) : "--"}
        </Typography>
      </Stack>
      {hasFirst && hasSecond && <DeltaValue first={first} second={second} formatter={formatter} />}
    </Stack>
  );
};

const BuildingTable = ({ primaryRows, comparisonRows, comparisonEnabled, primaryRange, comparisonRange, colors, onColorChange }) => {
  const primary = useMemo(() => buildBuildingSummaries(primaryRows), [primaryRows]);
  const comparison = useMemo(() => buildBuildingSummaries(comparisonRows), [comparisonRows]);
  const comparisonByBuilding = useMemo(
    () => new Map(comparison.map((row) => [row.building, row])),
    [comparison]
  );
  const primaryByBuilding = useMemo(
    () => new Map(primary.map((row) => [row.building, row])),
    [primary]
  );
  const buildings = useMemo(() => (
    [...new Set([...primaryByBuilding.keys(), ...comparisonByBuilding.keys()])]
      .sort((first, second) => first.localeCompare(second))
  ), [primaryByBuilding, comparisonByBuilding]);

  return (
    <Paper variant="outlined" sx={{ borderRadius: "6px", overflow: "hidden" }}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", lg: "center" },
          gap: 1.5,
        }}
      >
        <Box>
          <Typography sx={{ color: "#0A2240", fontSize: 16, fontWeight: 700 }}>Building data by carrier</Typography>
          <Typography sx={{ color: "text.secondary", fontSize: 12 }}>
            User counts are daily averages. Download and upload are totals across each date range.
          </Typography>
        </Box>
        {comparisonEnabled && (
          <Stack direction="row" alignItems="center" flexWrap="wrap" gap={2} aria-label="Comparison date legend">
            {[
              { key: "first", range: primaryRange, color: colors.first },
              { key: "second", range: comparisonRange, color: colors.second },
            ].map(({ key, range, color }) => (
              <Stack key={key} direction="row" alignItems="center" spacing={0.75}>
                <ColorDotPicker
                  color={color}
                  onChange={(hex) => onColorChange(key, hex)}
                  disabledColor={key === "first" ? colors.second : colors.first}
                />
                <Typography sx={{ color, fontSize: 12, fontWeight: 700 }}>{formatRange(range)}</Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </Box>
      <TableContainer sx={{ maxHeight: "70vh" }}>
        <Table stickyHeader size="small" aria-label="SU per building user data">
          <TableHead>
            <TableRow>
              <TableCell sx={{ bgcolor: "#0A2240", color: "white", fontWeight: 700, minWidth: 150 }}>Building</TableCell>
              <TableCell sx={{ bgcolor: "#0A2240", color: "white", fontWeight: 700, minWidth: 125 }}>Carrier</TableCell>
              <TableCell align="right" sx={{ bgcolor: "#0A2240", color: "white", fontWeight: 700, minWidth: 90 }}>
                Days{comparisonEnabled ? " (range 1 / 2)" : ""}
              </TableCell>
              <TableCell align="right" sx={{ bgcolor: "#0A2240", color: "white", fontWeight: 700, minWidth: comparisonEnabled ? 205 : 160 }}>
                Average daily users
              </TableCell>
              <TableCell align="right" sx={{ bgcolor: "#0A2240", color: "white", fontWeight: 700, minWidth: comparisonEnabled ? 205 : 150 }}>
                Download
              </TableCell>
              <TableCell align="right" sx={{ bgcolor: "#0A2240", color: "white", fontWeight: 700, minWidth: comparisonEnabled ? 205 : 150 }}>
                Upload
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {buildings.map((building) => {
              const first = primaryByBuilding.get(building);
              const second = comparisonByBuilding.get(building);
              return (
                <React.Fragment key={building}>
                  {CARRIER_ROWS.map((carrier, carrierIndex) => (
                    <TableRow
                      key={carrier.key}
                      hover
                      sx={{
                        bgcolor: carrier.key === "total" ? "#F5F7FA" : "white",
                        "& td": {
                          borderBottom: carrierIndex === CARRIER_ROWS.length - 1 ? "2px solid #CBD2D9" : undefined,
                        },
                      }}
                    >
                      {carrierIndex === 0 && (
                        <TableCell
                          rowSpan={CARRIER_ROWS.length}
                          sx={{ color: "#0A2240", fontWeight: 700, verticalAlign: "top", pt: 1.5 }}
                        >
                          {building}
                        </TableCell>
                      )}
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box sx={{ width: 4, height: 24, borderRadius: "2px", bgcolor: carrier.color, flexShrink: 0 }} />
                          <Typography sx={{ color: "#0A2240", fontSize: 13, fontWeight: 700 }}>{carrier.label}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {comparisonEnabled
                          ? `${first?.day_count ?? "--"} / ${second?.day_count ?? "--"}`
                          : first?.day_count ?? "--"}
                      </TableCell>
                      <TableCell align="right">
                        {comparisonEnabled ? (
                          <ComparisonMetric
                            colors={colors}
                            first={first?.[carrier.usersKey]}
                            second={second?.[carrier.usersKey]}
                            formatter={formatCount}
                          />
                        ) : (
                          <MetricValue value={first?.[carrier.usersKey]} formatter={formatCount} />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {comparisonEnabled ? (
                          <ComparisonMetric
                            colors={colors}
                            first={first?.[carrier.downloadKey]}
                            second={second?.[carrier.downloadKey]}
                            formatter={formatBytes}
                          />
                        ) : (
                          <MetricValue value={first?.[carrier.downloadKey]} formatter={formatBytes} />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {comparisonEnabled ? (
                          <ComparisonMetric
                            colors={colors}
                            first={first?.[carrier.uploadKey]}
                            second={second?.[carrier.uploadKey]}
                            formatter={formatBytes}
                          />
                        ) : (
                          <MetricValue value={first?.[carrier.uploadKey]} formatter={formatBytes} />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              );
            })}
            {!buildings.length && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 5, color: "text.secondary" }}>
                  No building records were found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

const LoadingDashboard = () => (
  <Stack spacing={2}>
    <Skeleton variant="rounded" height={150} />
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2 }}>
      <Skeleton variant="rounded" height={390} />
      <Skeleton variant="rounded" height={390} />
    </Box>
  </Stack>
);

export default function SUPerBuildingUserData() {
  const [dateSelectionMode, setDateSelectionMode] = useState("today");
  const [primaryRange, setPrimaryRange] = useState(createTodayRange);
  const [selectedBuildings, setSelectedBuildings] = useState([]);
  const allSelected = selectedBuildings.length === 0;
  const [comparisonColors, setComparisonColors] = useState(DEFAULT_COMPARISON_COLORS);
  const handleColorChange = (key, hex) => setComparisonColors((prev) => ({ ...prev, [key]: hex }));
  const [comparisonEnabled, setComparisonEnabled] = useState(false);
  const [comparisonRange, setComparisonRange] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const primaryApiRange = getApiRange(primaryRange);
  const comparisonApiRange = getApiRange(comparisonRange);
  const primaryQuery = useGetSuBuildingDataQuery(
    {
      ...primaryApiRange,
      buildings: selectedBuildings,
    },
    { pollingInterval: autoRefresh ? 600000 : 0 }
  );
  const comparisonQuery = useGetSuBuildingDataQuery(
    {
      startDate: comparisonApiRange?.startDate || "",
      endDate: comparisonApiRange?.endDate || "",
      buildings: selectedBuildings,
    },
    {
      skip: !comparisonEnabled || !comparisonApiRange,
      pollingInterval: autoRefresh ? 600000 : 0,
    }
  );

  const primaryRows = useMemo(
    () => normalizeBuildingRows(primaryQuery.data?.rows),
    [primaryQuery.data]
  );
  const comparisonRows = useMemo(
    () => normalizeBuildingRows(comparisonQuery.data?.rows),
    [comparisonQuery.data]
  );
  const availableBuildings = useMemo(
    () => getAvailableBuildings(primaryQuery.data, primaryRows),
    [primaryQuery.data, primaryRows]
  );
  const primarySummary = useMemo(() => buildOverallBuildingSummary(primaryRows), [primaryRows]);
  const comparisonSummary = useMemo(
    () => buildOverallBuildingSummary(comparisonRows),
    [comparisonRows]
  );

  const handleToday = () => {
    setDateSelectionMode("today");
    setPrimaryRange(createTodayRange());
  };

  const handlePrimaryRange = (range) => {
    if (!range?.start?._d || !range?.end?._d) return;
    setDateSelectionMode("range");
    setPrimaryRange(range);
  };

  const handleComparisonToggle = () => {
    if (comparisonEnabled) {
      setComparisonEnabled(false);
      setComparisonRange(null);
      return;
    }
    setComparisonEnabled(true);
  };

  const handleRefresh = () => {
    primaryQuery.refetch();
    if (comparisonEnabled && comparisonApiRange) comparisonQuery.refetch();
  };

  const handleDownload = () => {
    const ranges = [{ label: formatRange(primaryRange), rows: primaryRows }];
    if (comparisonEnabled && comparisonApiRange && comparisonRows.length) {
      ranges.push({ label: formatRange(comparisonRange), rows: comparisonRows });
    }
    const csv = buildBuildingCsv(ranges);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `su_building_data_${primaryApiRange.startDate}_${primaryApiRange.endDate}${
      comparisonEnabled && comparisonApiRange
        ? `_vs_${comparisonApiRange.startDate}_${comparisonApiRange.endDate}`
        : ""
    }.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const comparisonReady = comparisonEnabled && comparisonApiRange;
  const loading = primaryQuery.isLoading || (comparisonReady && comparisonQuery.isLoading);

  return (
    <Box sx={{ width: "100%", minWidth: 0, px: { xs: 1.5, sm: 2.5, lg: 3 }, py: 2.5 }}>
      <Stack spacing={2.5}>
        <Stack
          direction={{ xs: "column", lg: "row" }}
          alignItems={{ xs: "flex-start", lg: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <ApartmentIcon sx={{ color: "#005AA6" }} />
              <Typography component="h1" sx={{ color: "#0A2240", fontSize: { xs: 22, sm: 26 }, fontWeight: 700 }}>
                SU Per Building User Data
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.5 }}>
              <InfoOutlinedIcon sx={{ color: "text.secondary", fontSize: 16 }} />
              <Typography sx={{ color: "text.secondary", fontSize: 12 }}>
                Times are Eastern. Combined user counts sum per-building unique counts and may count a person in more than one building.
              </Typography>
            </Stack>
          </Box>

          <Stack direction="row" alignItems="center" spacing={0.5}>
            <AutorenewIcon sx={{ color: autoRefresh ? "#005AA6" : "text.secondary", fontSize: 18 }} />
            <FormControlLabel
              sx={{ mr: 0 }}
              control={(
                <Switch
                  checked={autoRefresh}
                  onChange={(event) => setAutoRefresh(event.target.checked)}
                  size="small"
                />
              )}
              label={<Typography sx={{ fontSize: 12 }}>Auto refresh</Typography>}
            />
          </Stack>
        </Stack>

        <Paper variant="outlined" sx={{ borderRadius: "6px", p: { xs: 1.5, sm: 2 } }}>
          <Stack spacing={1.5}>
            <Stack
              direction={{ xs: "column", xl: "row" }}
              alignItems={{ xs: "stretch", xl: "center" }}
              justifyContent="space-between"
              spacing={1.5}
            >
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={[ALL_BUILDINGS, ...availableBuildings]}
                value={allSelected ? [ALL_BUILDINGS] : selectedBuildings}
                onChange={(_, value) => {
                  const clickedAll = value.includes(ALL_BUILDINGS) && !allSelected;
                  setSelectedBuildings(clickedAll ? [] : value.filter((option) => option !== ALL_BUILDINGS));
                }}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
                    {option}
                  </li>
                )}
                renderTags={(value, getTagProps) => (
                  <>
                    {value.slice(0, 2).map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option}
                        label={option}
                        size="small"
                        onDelete={option === ALL_BUILDINGS ? undefined : getTagProps({ index }).onDelete}
                      />
                    ))}
                    {value.length > 2 && <Chip label={`+${value.length - 2}`} size="small" />}
                  </>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Buildings"
                    placeholder=""
                    size="small"
                  />
                )}
                sx={{ width: { xs: "100%", xl: 430 } }}
              />

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
                <Stack direction="row">
                  <Button
                    variant={dateSelectionMode === "today" ? "contained" : "outlined"}
                    onClick={handleToday}
                    sx={{ borderRadius: "4px 0 0 4px", textTransform: "none", whiteSpace: "nowrap", height: 40 }}
                  >
                    Today
                  </Button>
                  <MuiDateRangePicker
                    isActive={dateSelectionMode === "range"}
                    onChange={handlePrimaryRange}
                    dateSelectionMode={dateSelectionMode}
                    selectedVerticleTab={0}
                  />
                </Stack>
                <Button
                  variant={comparisonEnabled ? "contained" : "outlined"}
                  startIcon={<CompareArrowsIcon />}
                  onClick={handleComparisonToggle}
                  sx={{ height: 40, textTransform: "none", whiteSpace: "nowrap" }}
                >
                  Compare
                </Button>
                <Tooltip title="Refresh data">
                  <Button
                    variant="outlined"
                    onClick={handleRefresh}
                    disabled={primaryQuery.isFetching || comparisonQuery.isFetching}
                    aria-label="Refresh data"
                    sx={{ minWidth: 40, width: { xs: "100%", sm: 40 }, height: 40, px: 1 }}
                  >
                    {primaryQuery.isFetching || comparisonQuery.isFetching
                      ? <CircularProgress size={18} />
                      : <RefreshIcon />}
                  </Button>
                </Tooltip>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownload}
                  disabled={!primaryRows.length || (comparisonReady && comparisonQuery.isFetching)}
                  sx={{ height: 40, textTransform: "none", whiteSpace: "nowrap" }}
                >
                  CSV
                </Button>
              </Stack>
            </Stack>

            {comparisonEnabled && (
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="flex-end"
                spacing={1}
              >
                <Typography sx={{ color: "#0A2240", fontSize: 13, fontWeight: 700 }}>Compare with</Typography>
                <Box sx={{ display: "flex", justifyContent: { xs: "stretch", sm: "flex-end" } }}>
                  <MuiDateRangePicker
                    isActive={Boolean(comparisonApiRange)}
                    onChange={(range) => {
                      if (range?.start?._d && range?.end?._d) setComparisonRange(range);
                    }}
                    dateSelectionMode="range"
                    selectedVerticleTab={0}
                  />
                </Box>
                <Tooltip title="Close comparison">
                  <Button
                    variant="text"
                    onClick={handleComparisonToggle}
                    aria-label="Close comparison"
                    sx={{ minWidth: 40, width: { xs: "100%", sm: 40 }, height: 40, px: 1 }}
                  >
                    <CloseIcon />
                  </Button>
                </Tooltip>
              </Stack>
            )}
          </Stack>
        </Paper>

        {primaryQuery.error && <Alert severity="error">{getErrorMessage(primaryQuery.error)}</Alert>}
        {comparisonReady && comparisonQuery.error && (
          <Alert severity="error">Comparison: {getErrorMessage(comparisonQuery.error)}</Alert>
        )}

        {loading ? (
          <LoadingDashboard />
        ) : (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: comparisonReady ? { xs: "1fr", xl: "repeat(2, minmax(0, 1fr))" } : "1fr",
                gap: 2,
              }}
            >
              <SummaryPanel label="Primary range" range={primaryRange} summary={primarySummary} accent={comparisonReady ? comparisonColors.first : undefined} />
              {comparisonReady && (
                <SummaryPanel
                  label="Comparison range"
                  range={comparisonRange}
                  summary={comparisonSummary}
                  accent={comparisonColors.second}
                />
              )}
            </Box>

            <BuildingTable
              primaryRows={primaryRows}
              comparisonRows={comparisonRows}
              comparisonEnabled={Boolean(comparisonReady)}
              primaryRange={primaryRange}
              comparisonRange={comparisonRange}
              colors={comparisonColors}
              onColorChange={handleColorChange}
            />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: comparisonReady ? { xs: "1fr", xl: "repeat(2, minmax(0, 1fr))" } : "1fr",
                gap: 2,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                {comparisonReady && (
                  <Typography sx={{ color: comparisonColors.first, fontSize: 14, fontWeight: 700, mb: 1 }}>
                    Primary: {formatRange(primaryRange)}
                  </Typography>
                )}
                <RangeCharts rows={primaryRows} compact={Boolean(comparisonReady)} />
              </Box>
              {comparisonReady && (
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ color: comparisonColors.second, fontSize: 14, fontWeight: 700, mb: 1 }}>
                    Comparison: {formatRange(comparisonRange)}
                  </Typography>
                  <RangeCharts rows={comparisonRows} compact />
                </Box>
              )}
            </Box>
          </>
        )}
      </Stack>
    </Box>
  );
}